import {
  AnalyticsConfigV1,
  ComplianceConfigV1,
  DeviceConfigV2,
  HomeSupplyConfigV1,
  NotificationJobConfigV1,
  PatientConfigV1,
  PatientStatusConfigV1,
  PatientWebSettingsConfigV1,
  RawAssessmentConsequenceActivityCardConfigV1,
  RawCspFeatureConfigV2,
  RawNotificationConfigV1,
  RawPatientCopingTipConfigV1,
  RawStudyMetaConfigV1,
  RequestTagsStudyConfigV1,
  RpmConfigV2,
  StudyArchivingConfigV1,
  StudyMetricsConfigV1,
  WebPortalServerConfigV1,
} from '@csp/config-schemas';
import { WebPortalClientConfigV1 } from '@csp/config-schemas/src/lib/client/model/generated/WebPortalClientConfigV1';
import { StudyConfigRestServiceV2 } from '@csp/csp-api-client';
import { ApiOptions, Maybe } from '@csp/csp-common-model';
import { toRestOptions } from '@csp/csp-fe-auth';
import { RecommendedResourcesConfigV1, StudyConfigurationEntryV2 } from '@csp/dto';
import { WebPortalServerConfigService } from '../../server/service/WebPortalServerConfigService';
import { AnalyticsConfigService } from '../../analytics/service/AnalyticsConfigService';
import { AssessmentConsequenceActivityCardService } from '../../assessment-consequence-activity-card/AssessmentConsequenceActivityCardService';
import { WebPortalClientConfigService } from '../../client/service/WebPortalClientConfigService';
import { ComplianceConfigService } from '../../compliance/service/ComplianceConfigService';
import { DeviceConfigServiceV1 } from '../../device/service/DeviceConfigServiceV1';
import { FeatureToggleFactory } from '../../feature-toggle/model/FeatureToggleFactory';
import { NotificationConfigService } from '../../notification-config/NotificationConfigService';
import { NotificationJobConfigServiceV1 } from '../../notification-job/service/NotificationJobConfigServiceV1';
import { PatientCopingTipService } from '../../patient-care-tip/PatientCopingTipService';
import { PatientConfigServiceV1 } from '../../patient/service/PatientConfigServiceV1';
import { PatientStatusConfigServiceV1 } from '../../patient/service/PatientStatusConfigServiceV1';
import { RecommendedResourcesConfigService } from '../../recommended-resources-config/service/RecommendedResourcesConfigService';
import { ReportsConfigService } from '../../reports-config/service/ReportsConfigService';
import { RequestTagsStudyConfigService } from '../../request-tags-config/service/RequestTagsStudyConfigSerivce';
import { RpmConfigService } from '../../rpm/service/RpmConfigService';
import { StudyArchivingConfigService } from '../../study-archiving-config/service/StudyArchivingConfigService';
import { StudyMetricsConfigService } from '../../study-metrics/service/StudyMetricsConfigService';
import { StudyConfig } from '../model/StudyConfig';
import { StudyDefinitionCodes } from '../model/StudyDefinitionCodes';
import { HomeSupplyStudyConfigService } from '../../home-supply/HomeSupplyStudyConfigService';
import { EmailJobConfigs } from '../../email-job/model/EmailJobConfigs';
import { PatientWebSettingsConfigService } from '../../patient-web-settings-config/PatientWebSettingsConfigService';
import { StudyMetaService } from '../../study-meta/StudyMetaConfigService';

// TODO Change to return Maybe<T> to indicate that there might not be a matching config available
export const getConfigurationByCode = <T>(
  entries: StudyConfigurationEntryV2[],
  definitionCode: StudyDefinitionCodes,
): T => entries.find(e => e.definitionCode === definitionCode)?.data as T;

let currentConfiguration: Maybe<StudyConfig>;

const getStudyConfig = (): Maybe<StudyConfig> => currentConfiguration;

const setStudyConfig = (studyConfig: StudyConfig): void => {
  currentConfiguration = studyConfig;
};

// TODO Works for 1.2 and 2.0 but will break in the future if we add new implicit dependencies in StudyConfigurationEntryV2
const toStudyConfig = (entries: StudyConfigurationEntryV2[]): StudyConfig => {
  const patientConfigV1 = getConfigurationByCode<PatientConfigV1>(entries, 'PATIENT_CONFIG');
  const rawFeatureConfig = getConfigurationByCode<RawCspFeatureConfigV2>(entries, 'FEATURE_TOGGLE_CONFIG');
  const patientStatusConfigV1 = getConfigurationByCode<PatientStatusConfigV1>(entries, 'PATIENT_STATUS_CONFIG');
  const metricsConfig = getConfigurationByCode<StudyMetricsConfigV1>(entries, 'METRICS_CONFIG');
  const notificationJobConfigV1 = getConfigurationByCode<NotificationJobConfigV1>(entries, 'NOTIFICATION_JOB_CONFIG');
  const deviceConfigV2 = getConfigurationByCode<DeviceConfigV2>(entries, 'DEVICE_CONFIG');
  const complianceConfigV1 = getConfigurationByCode<ComplianceConfigV1>(entries, 'COMPLIANCE_CONFIG');
  const patientWebSettingsConfigV1 = getConfigurationByCode<PatientWebSettingsConfigV1>(
    entries,
    'PATIENT_WEB_SETTINGS_CONFIG',
  );
  const webPortalClientConfigV1 = getConfigurationByCode<WebPortalClientConfigV1>(entries, 'WEB_PORTAL_CLIENT_CONFIG');
  const webPortalServerConfigV1 = getConfigurationByCode<WebPortalServerConfigV1>(entries, 'WEB_PORTAL_SERVER_CONFIG');
  const reportsConfigV1 = getConfigurationByCode<ComplianceConfigV1>(entries, 'REPORTS_CONFIG');
  const requestTagConfigV1 = getConfigurationByCode<RequestTagsStudyConfigV1>(entries, 'REQUEST_TAG_CONFIG');
  const rpmConfigV2 = getConfigurationByCode<RpmConfigV2>(entries, 'RPM_CONFIG');
  const rawPatientCopingTipConfigV1 = getConfigurationByCode<Maybe<RawPatientCopingTipConfigV1>>(
    entries,
    'PATIENT_COPING_TIP_CONFIG',
  );
  const recommendedResourcesConfigV1 = getConfigurationByCode<Maybe<RecommendedResourcesConfigV1>>(
    entries,
    'RECOMMENDED_RESOURCES_CONFIG',
  );
  const assessmentConsequenceActivityCardConfigV1 = getConfigurationByCode<
    Maybe<RawAssessmentConsequenceActivityCardConfigV1>
  >(entries, 'ASSESSMENT_CONSEQUENCE_ACTIVITY_CARD');
  const analyticsConfigV1 = getConfigurationByCode<Maybe<AnalyticsConfigV1>>(entries, 'ANALYTICS_CONFIG');
  const studyArchivingConfigV1 = getConfigurationByCode<StudyArchivingConfigV1>(entries, 'STUDY_ARCHIVING_CONFIG');
  const rawNotificationConfigV1 = getConfigurationByCode<Maybe<RawNotificationConfigV1>>(
    entries,
    'NOTIFICATION_CONFIG',
  );
  const homeSupplyConfigV1 = getConfigurationByCode<HomeSupplyConfigV1>(entries, 'HOME_SUPPLY_CONFIG');
  const studyMetaConfigV1 = getConfigurationByCode<Maybe<RawStudyMetaConfigV1>>(entries, 'STUDY_META_CONFIG');

  const featureConfig = FeatureToggleFactory.from(rawFeatureConfig);
  const patientConfig = PatientConfigServiceV1.toPatientConfig(patientConfigV1);
  const patientStatusMappings = PatientStatusConfigServiceV1.toPatientStatusConfiguration(patientStatusConfigV1);
  StudyMetricsConfigService.validateMetricsConfig(metricsConfig);
  const { healthEventNotificationJobConfigs, patientInactiveEmailConfigs } =
    NotificationJobConfigServiceV1.mapNotificationJobConfig(notificationJobConfigV1);
  const deviceConfig = DeviceConfigServiceV1.toDeviceConfig(deviceConfigV2);
  const complianceConfig = ComplianceConfigService.toComplianceConfig(complianceConfigV1);
  const patientComplianceEmails = ComplianceConfigService.toEmailJobConfigs(complianceConfigV1);
  const patientWebSettingsConfig =
    PatientWebSettingsConfigService.toPatientWebSettingsConfig(patientWebSettingsConfigV1);
  const webPortalClientConfig = WebPortalClientConfigService.toWebPortalClientConfig(webPortalClientConfigV1);
  const webPortalServerConfig = WebPortalServerConfigService.toWebPortalServerConfig(webPortalServerConfigV1);
  const reportsConfig = ReportsConfigService.toReportsConfig(reportsConfigV1);
  const requestTagsConfig = RequestTagsStudyConfigService.toRequestTagsStudyConfig(requestTagConfigV1);
  const rpmConfig = RpmConfigService.toRpmConfig(rpmConfigV2);
  const { rpmMonitoringEmails, rpmSummaryEmails, rpmSingleSymptomEmail } = RpmConfigService.toEmailConfig(rpmConfigV2);
  const patientCopingTipConfig = PatientCopingTipService.toPatientCopingTipConfig(rawPatientCopingTipConfigV1);
  const recommendedResourcesConfig =
    RecommendedResourcesConfigService.toRecommendedResourcesConfig(recommendedResourcesConfigV1);
  const assessmentConsequenceActivityCardConfig =
    AssessmentConsequenceActivityCardService.toAssessmentConsequenceActivityCardConfig(
      assessmentConsequenceActivityCardConfigV1,
    );

  const analyticsConfig = AnalyticsConfigService.toAnalyticsConfig(analyticsConfigV1);
  const studyArchivingConfig = StudyArchivingConfigService.toStudyArchivingConfig(studyArchivingConfigV1);
  const notificationConfig = NotificationConfigService.toNotificationConfig(rawNotificationConfigV1);
  const homeSupplyConfig = HomeSupplyStudyConfigService.toHomeSupplyStudyConfig(homeSupplyConfigV1);
  const studyMetaConfig = StudyMetaService.toStudyMetaConfig(studyMetaConfigV1);

  const emailJobConfigs: EmailJobConfigs = {
    patientInactiveEmails: patientInactiveEmailConfigs,
    patientComplianceEmails,
    rpmSummaryEmails,
    rpmMonitoringEmails,
    rpmSingleSymptomEmail,
  };

  return {
    analyticsConfig,
    featureConfig,
    metricsConfig,
    questionnaireConfig: patientConfig.questionnaire,
    patientValidation: patientConfig.validation,
    periodConfig: patientConfig.period,
    rescreenConfig: patientConfig.rescreen,
    visitConfig: patientConfig.visit,
    hbsConfig: patientConfig.hbs,
    authenticationConfig: patientConfig.authentication,
    patientWebSettingsConfig,
    webPortalClientConfig,
    webPortalServerConfig,
    patientStatusMappings,
    healthEventNotificationJobConfigs,
    deviceConfig,
    complianceConfig,
    reportsConfig,
    requestTagsConfig,
    rpmConfig,
    patientCopingTipConfig,
    recommendedResourcesConfig,
    assessmentConsequenceActivityCardConfig,
    mobileAppConfig: patientConfig.mobileApp,
    studyArchivingConfig,
    notificationConfig,
    homeSupplyConfig,
    emailJobConfigs,
    studyMetaConfig,
  };
};

const fetchStudyConfig = async (apiOptions?: ApiOptions): Promise<StudyConfig> => {
  const configs = await StudyConfigRestServiceV2.getConfigs(toRestOptions(apiOptions));
  return toStudyConfig(configs);
};

const refreshStudyConfig = async (apiOptions?: ApiOptions): Promise<StudyConfig> => {
  currentConfiguration = await fetchStudyConfig(toRestOptions(apiOptions));
  return currentConfiguration;
};

export const StudyConfigService = {
  getStudyConfig,
  setStudyConfig,
  refreshStudyConfig,
  fetchStudyConfig,
  toStudyConfig,
};
